import type { TypeFilterOption, TypeFilterSection } from '@/types/list/types.list.filter';
import type { TypeListFacet, TypeListFindConfig, TypeListFindState } from '@/types/list/types.list.find';
import { listFacetValues } from '@/function/list/list.facet.values';
import { listFindMatch } from '@/function/list/list.find.match';

const SEARCHABLE_FROM = 12;

const tally = <T>(items: readonly T[], facet: TypeListFacet<T>): Map<string, number> => {
    const counts = new Map<string, number>();
    if (!facet.of) return counts;
    for (const item of items) {
        for (const value of new Set(listFacetValues(facet.of(item)))) counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    return counts;
};

export const listFindSections = <T>(
    items: readonly T[],
    config: TypeListFindConfig<T>,
    state: TypeListFindState,
    now: Date = new Date(),
): TypeFilterSection[] => {
    const local = config.local !== false;

    return (config.facets ?? []).map((facet) => {
        const overall = tally(items, facet);
        const context = local && facet.of ? tally(listFindMatch(items, config, state, now, facet.id), facet) : overall;
        const labelOf = (value: string) => facet.labelOf?.(value) ?? value;

        const base: TypeFilterOption[] = facet.options
            ? facet.options.map((option) => ({ ...option }))
            : [...overall.keys()]
                .sort((a, b) => (overall.get(b) ?? 0) - (overall.get(a) ?? 0) || labelOf(a).localeCompare(labelOf(b), 'it'))
                .map((value) => ({ value, label: labelOf(value) }));

        const selected = state.active[facet.id] ?? [];
        const options = base
            .filter((option) => facet.keepEmpty || !facet.of || (overall.get(option.value) ?? 0) > 0 || selected.includes(option.value))
            .map((option) => ({
                ...option,
                count: facet.of ? context.get(option.value) ?? 0 : option.count,
                dot: option.dot ?? facet.dotOf?.(option.value),
            }));

        return {
            id: facet.id,
            label: facet.label,
            options,
            searchable: facet.searchable ?? options.length >= SEARCHABLE_FROM,
            negatable: local && Boolean(facet.of) && facet.negatable !== false && !facet.single,
        };
    });
};

export default listFindSections;
